import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "../../config/config";
import { getAuthToken } from "../../utils/token";
import { toast } from "react-toastify";

const ProductionPlan12Month = ({
    month,
    year,
    search = "",
}) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const formatNumber = (value) =>
        Number(value || 0).toLocaleString("en-IN");

    const getMonths = () => {
        const monthsMap = new Map();

        rows.forEach((item) => {
            if (!item?.plan_month) return;

            const monthKey = String(item.plan_month).substring(0, 7);

            if (!monthsMap.has(monthKey)) {
                const [year, month] = monthKey.split("-");

                const date = new Date(
                    Number(year),
                    Number(month) - 1,
                    1
                );

                monthsMap.set(monthKey, {
                    key: monthKey,
                    label: date.toLocaleString("en-US", {
                        month: "short",
                        year: "2-digit",
                    }),
                });
            }
        });

        return [...monthsMap.values()].sort((a, b) =>
            a.key.localeCompare(b.key)
        );
    };

    const months = getMonths();

    const getReport = async () => {
        try {
            setLoading(true);

            const token = getAuthToken();

            const response = await axios.post(
                `${config.API}/senmanager/production12month`,
                {
                    month: Number(month),
                    year: Number(year),
                    search: search || null,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response?.data?.success) {
                setRows(response?.data?.data || []);
            } else {
                setRows([]);

                toast.warning(
                    response?.data?.message ||
                    "No production plan data found."
                );
            }
        } catch (error) {
            console.error(
                "12 Month Production Plan:",
                error
            );

            setRows([]);

            toast.error(
                error?.response?.data?.message ||
                "Unable to load 12 month production plan."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (month && year) {
            getReport();
        }
    }, [month, year, search]);

    /*
     * Convert API response:
     *
     * Product 1 - Jan
     * Product 1 - Feb
     * Product 1 - Mar
     *
     * INTO:
     *
     * Product 1
     * Jan | Feb | Mar | ...
     */
    const groupedRows = Object.values(
        rows.reduce((acc, item) => {
            const productId = item?.product_id;

            if (!productId) {
                return acc;
            }

            if (!acc[productId]) {
                acc[productId] = {
                    product_id: productId,
                    item_code:
                        item?.product?.product_code || "-",
                    product_name:
                        item?.product?.name || "-",
                    values: {},
                };
            }

            const monthKey = String(
                item?.plan_month || ""
            ).substring(0, 7);

            acc[productId].values[monthKey] =
                Number(item?.qty_produced || 0);

            return acc;
        }, {})
    );

    return (
        <div className="container-fluid px-0">

            <div className="card border-0 shadow-sm">

                {/* HEADER */}
                <div className="card-header bg-white border-0 px-4 py-3">
                    <div className="d-flex justify-content-between align-items-center">

                        <div>
                            <h5
                                className="mb-1 fw-bold"
                                style={{ color: "#17365d" }}
                            >
                                12-Month Production Plan
                            </h5>

                            <small className="text-muted">
                                Production plan overview for{" "}
                                {new Date(
                                    Number(year),
                                    Number(month) - 1
                                ).toLocaleString("en-US", {
                                    month: "long",
                                })}{" "}
                                {year}
                            </small>
                        </div>

                        <div>
                            <span className="badge bg-primary-subtle text-primary px-3 py-2">
                                12 Month View
                            </span>
                        </div>

                    </div>
                </div>

                {/* TABLE */}
                <div className="table-responsive">

                    <table
                        className="table table-hover align-middle mb-0"
                        style={{
                            minWidth: "1500px",
                            fontSize: "13px",
                        }}
                    >

                        <thead>
                            <tr
                                style={{
                                    background: "#f1f5f9",
                                    color: "#17365d",
                                }}
                            >

                                <th
                                    className="text-start px-4 py-3"
                                    style={{
                                        width: "110px",
                                        whiteSpace: "nowrap",
                                        fontWeight: 700,
                                    }}
                                >
                                    Item Code
                                </th>

                                <th
                                    className="text-start py-3"
                                    style={{
                                        minWidth: "380px",
                                        whiteSpace: "nowrap",
                                        fontWeight: 700,
                                    }}
                                >
                                    Product
                                </th>

                                {months.map((month) => (
                                    <th
                                        key={month.key}
                                        className="text-end py-3"
                                        style={{
                                            minWidth: "75px",
                                            whiteSpace: "nowrap",
                                            fontWeight: 700,
                                        }}
                                    >
                                        {month.label}
                                    </th>
                                ))}

                                <th
                                    className="text-end px-4 py-3"
                                    style={{
                                        minWidth: "110px",
                                        whiteSpace: "nowrap",
                                        fontWeight: 700,
                                    }}
                                >
                                    Total
                                </th>

                            </tr>
                        </thead>

                        <tbody>

                            {loading ? (

                                <tr>
                                    <td
                                        colSpan={months.length + 3}
                                        className="text-center py-5"
                                    >

                                        <div
                                            className="spinner-border text-primary"
                                            role="status"
                                        />

                                        <div className="text-muted mt-3">
                                            Loading 12-month production plan...
                                        </div>

                                    </td>
                                </tr>

                            ) : groupedRows.length > 0 ? (

                                groupedRows.map((item, index) => {

                                    const values = months.map(
                                        (month) =>
                                            Number(
                                                item.values[
                                                month.key
                                                ] || 0
                                            )
                                    );

                                    const total = values.reduce(
                                        (sum, value) =>
                                            sum + value,
                                        0
                                    );

                                    return (
                                        <tr
                                            key={`production-${item.product_id}-${index}`}
                                        >

                                            {/* ITEM CODE */}

                                            <td className="px-4 fw-bold text-dark">
                                                <span
                                                    className="badge bg-light text-dark border px-3 py-2"
                                                    style={{
                                                        fontSize: "14px",
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {item.item_code}
                                                </span>
                                            </td>

                                            {/* PRODUCT */}

                                            <td
                                                className="fw-semibold text-dark"
                                                style={{
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {item.product_name}
                                            </td>

                                            {/* MONTHS */}

                                            {values.map(
                                                (
                                                    value,
                                                    monthIndex
                                                ) => (

                                                    <td
                                                        key={`${item.product_id}-${months[monthIndex].key}`}
                                                        className="text-end"
                                                    >

                                                        {value > 0 ? (
                                                            <span className="fw-semibold">
                                                                {formatNumber(
                                                                    value
                                                                )}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">
                                                                -
                                                            </span>
                                                        )}

                                                    </td>

                                                )
                                            )}

                                            {/* TOTAL */}

                                            <td className="text-end px-4">

                                                <span
                                                    className="badge bg-primary-subtle text-primary fw-bold px-3 py-2"
                                                >
                                                    {formatNumber(total)}
                                                </span>

                                            </td>

                                        </tr>
                                    );
                                })

                            ) : (

                                <tr>
                                    <td
                                        colSpan={months.length + 3}
                                        className="text-center text-muted py-5"
                                    >
                                        <div className="mb-2">
                                            <i className="bi bi-calendar-x fs-3" />
                                        </div>

                                        No production plan data found.
                                    </td>
                                </tr>

                            )}

                        </tbody>

                        {/* FOOTER TOTAL */}

                        {groupedRows.length > 0 && !loading && (
                            <tfoot>

                                <tr
                                    style={{
                                        background: "#f8fafc",
                                        borderTop:
                                            "2px solid #dee2e6",
                                    }}
                                >

                                    <th
                                        colSpan="2"
                                        className="text-start px-4 py-3"
                                        style={{
                                            color: "#17365d",
                                        }}
                                    >
                                        Total Production
                                    </th>

                                    {months.map(
                                        (month) => {

                                            const monthTotal =
                                                groupedRows.reduce(
                                                    (
                                                        total,
                                                        item
                                                    ) =>
                                                        total +
                                                        Number(
                                                            item
                                                                .values[
                                                            month
                                                                .key
                                                            ] || 0
                                                        ),
                                                    0
                                                );

                                            return (
                                                <th
                                                    key={`total-${month.key}`}
                                                    className="text-end py-3"
                                                >
                                                    {monthTotal > 0
                                                        ? formatNumber(
                                                            monthTotal
                                                        )
                                                        : "-"}
                                                </th>
                                            );
                                        }
                                    )}

                                    <th className="text-end px-4 py-3">
                                        {formatNumber(
                                            groupedRows.reduce(
                                                (
                                                    total,
                                                    item
                                                ) =>
                                                    total +
                                                    Object.values(
                                                        item.values
                                                    ).reduce(
                                                        (
                                                            sum,
                                                            value
                                                        ) =>
                                                            sum +
                                                            Number(
                                                                value ||
                                                                0
                                                            ),
                                                        0
                                                    ),
                                                0
                                            )
                                        )}
                                    </th>

                                </tr>

                            </tfoot>
                        )}

                    </table>

                </div>

            </div>
        </div>
    );
};

export default ProductionPlan12Month;